import React from 'react';
import Button from 'kingpin/atoms/Button';
import Tooltip from 'components/Tooltip';
import Row from '../../Common/Row';

const formatCount = (count: number) => count.toLocaleString();

const getCountLabel = ({
  count,
  ownCount,
}: {
  count?: number;
  ownCount?: number;
}) => {
  if (count === undefined) {
    return undefined;
  }
  if (ownCount === undefined || ownCount === count) {
    return formatCount(count);
  }
  return `${formatCount(count)} of ${formatCount(ownCount)}`;
};

export const QuickFilter = ({
  onClick,
  isSelected,
  name,
  isDisabled,
  isDisabledMessage,
  count,
  ownCount,
  isCountLoading,
}: {
  onClick: () => void;
  isSelected: boolean;
  name: string;
  isDisabled?: boolean;
  isDisabledMessage?: string;
  /**
   * Records the grid would show with this filter applied alongside the
   * current selection
   */
  count?: number;
  /**
   * Records matching this filter on its own (ignoring the other selected
   * quick filters)
   */
  ownCount?: number;
  isCountLoading?: boolean;
}) => {
  const button = (
    <Button
      type={isSelected ? 'Primary' : 'Secondary'}
      size="Small"
      label={name}
      trailingLabel={getCountLabel({ count, ownCount })}
      isTrailingLabelLoading={isCountLoading}
      onClick={onClick}
      testId={`filter-${name}`}
      isDisabled={isDisabled}
      isDisabledMessage={isDisabledMessage}
    />
  );

  if (
    !isCountLoading &&
    count !== undefined &&
    ownCount !== undefined &&
    count !== ownCount &&
    !isDisabled
  ) {
    return (
      <Tooltip
        content={`${formatCount(count)} records with your other quick filters · ${formatCount(ownCount)} with this filter alone`}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};

const QuickFilters = ({
  quickFilters,
  onFilterToggled,
  selectedQuickFilters,
  countsById,
}: {
  quickFilters: LegacySavedFilter[];
  onFilterToggled: (f: LegacySavedFilter) => void;
  selectedQuickFilters: LegacySavedFilter[];
  countsById?: QuickFilterCounts.CountsById;
}) => (
  <Row centerAlign style={{ gap: 8, flexShrink: 0 }}>
    {quickFilters.map((f) => (
      <QuickFilter
        key={f.id}
        onClick={() => onFilterToggled(f)}
        isSelected={selectedQuickFilters.some((sf) => sf.id === f.id)}
        name={f.name || 'Filter'}
        count={countsById?.[f.id]?.count}
        ownCount={countsById?.[f.id]?.ownCount}
        isCountLoading={countsById?.[f.id]?.isLoading}
      />
    ))}
  </Row>
);

export default QuickFilters;
